// Shared changelog commit classifier — turns commit subjects into a
// (label, route) pair so both the Changelog page and the header "What's new"
// pill render the same module pills + deep-link buttons.
//
// Two layers:
//   1. Strict prefix:   "WhatsApp: foo"  →  module = "WhatsApp"
//   2. Keyword fallback: scan title for module names so older / sloppier
//      commits ("WA bubble: ...", "Add fix for tickets queue") still get
//      classified. Order matters — first match wins.

// Canonical module → frontend route. Keys are lowercase for lookup.
export const MODULE_TO_ROUTE = {
  whatsapp: '/whatsapp-settings',
  wa: '/whatsapp-settings',
  tickets: '/tickets',
  ticket: '/tickets',
  invoicing: '/invoicing',
  invoice: '/invoicing',
  hr: '/hr',
  payroll: '/hr',
  reports: '/reports',
  report: '/reports',
  calendar: '/calendar',
  chat: '/chat',
  vault: '/vault',
  monitor: '/monitor',
  monitoring: '/monitoring',
  topology: '/topology',
  cctv: '/cctv',
  assets: '/assets',
  asset: '/assets',
  supplies: '/supplies',
  toner: '/supplies',
  'spare parts': '/spare-parts',
  workshop: '/workshop',
  laboratorio: '/workshop',
  procedures: '/procedures',
  'file permissions': '/file-permissions',
  vpn: '/vpn',
  'access control': '/access-control',
  budgets: '/budgets',
  budget: '/budgets',
  kb: '/kb',
  'knowledge base': '/kb',
  'rag search': '/rag-search',
  rag: '/rag-search',
  'remote access': '/remote-access',
  'remote assist': '/remote-access',
  deployments: '/deployments',
  deployment: '/deployments',
  projects: '/projects',
  project: '/projects',
  personnel: '/personnel',
  errands: '/field-tasks',
  'field tasks': '/field-tasks',
  recorridas: '/field-tasks',
  agents: '/agents',
  agent: '/agents',
  'agent activity': '/agent-activity',
  'agent ratings': '/agent-ratings',
  'agent status': '/agent-status',
  game: '/game-admin',
  gamification: '/game-admin',
  feedback: '/feedback',
  changelog: '/changelog',
  companies: '/companies',
  company: '/companies',
  contacts: '/companies',
  contact: '/companies',
  'email accounts': '/email-accounts',
  email: '/email-accounts',
  inbound: '/inbound-rules',
  'inbound rules': '/inbound-rules',
  'audit log': '/audit-log',
  diagnostics: '/diagnostics',
  'backup monitor': '/backup-monitor',
  backups: '/backup-monitor',
  'comms monitor': '/comms-monitor',
  comms: '/comms-monitor',
  notifications: '/notifications',
  'telegram routes': '/telegram-routes',
  telegram: '/telegram-routes',
  'ai dashboard': '/ai-dashboard',
  ai: '/ai-dashboard',
  configurations: '/configurations',
  '2fa codes': '/2fa-codes',
  '2fa': '/2fa-codes',
  papelera: '/papelera',
  spam: '/spam',
  trash: '/trash',
  validation: '/validation',
  'hours review': '/report-review',
  'report review': '/report-review',
  dashboard: '/',
  profile: '/profile',
  settings: '/settings',
  infrastructure: '/monitor',
  infra: '/monitor',
  unifi: '/monitor',
  printers: '/supplies',
  printer: '/supplies',
  ipc: '/settings',
  showcase: '/demo',
  demo: '/demo',
};

// Keyword scan order. Multi-word phrases first so they win over single words
// (e.g. "file permissions" must match before "file" or "permissions").
const KEYWORD_ORDER = Object.keys(MODULE_TO_ROUTE).sort((a, b) => b.length - a.length);

// Pretty label for a normalized module key.
export function moduleLabel(key) {
  if (!key) return null;
  return key
    .split(' ')
    .map(w => w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

// Extract module + remaining title text.
// Returns { moduleKey, label, route, rest } where moduleKey/label/route may be null.
// `rest` drops the trailing "— reason: …" of the commit subject: that part is
// written for developers and filled the Novedades rows (Thiago, 22-oct-2026).
// The full text is still on the expanded entry (commit_message).
export function classify(title) {
  const out = classifyRaw(title);
  return { ...out, rest: (out.rest || '').split(/\s+[—-]+\s+reason:/i)[0] };
}

function classifyRaw(title) {
  if (!title) return { moduleKey: null, label: null, route: null, rest: '' };

  // 1. Strict "Module: rest" prefix
  const m = /^([A-Z][A-Za-z0-9 /+&.-]{1,30}):\s*(.+)$/.exec(title);
  if (m) {
    const declared = m[1].trim();
    const rest = m[2];
    const lookup = declared.toLowerCase();
    // Try the declared module as-is, then strip suffixes ("WhatsApp bubble" → "whatsapp")
    let route = MODULE_TO_ROUTE[lookup] || null;
    if (!route) {
      for (const k of KEYWORD_ORDER) {
        if (lookup.startsWith(k + ' ') || lookup === k) { route = MODULE_TO_ROUTE[k]; break; }
      }
    }
    return { moduleKey: lookup, label: declared, route, rest };
  }

  // 2. Keyword fallback — scan whole title
  const haystack = title.toLowerCase();
  for (const k of KEYWORD_ORDER) {
    // Word-boundary match so "asset" doesn't match "assets" twice in opposite order
    const re = new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(haystack)) {
      return { moduleKey: k, label: moduleLabel(k), route: MODULE_TO_ROUTE[k], rest: title };
    }
  }

  return { moduleKey: null, label: null, route: null, rest: title };
}

export function moduleColor(mod) {
  if (!mod) return { bg: 'var(--c-surface-2)', fg: 'var(--c-text-muted)' };
  let hash = 0;
  for (let i = 0; i < mod.length; i++) hash = (hash * 31 + mod.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return { bg: `hsl(${hue} 70% 92%)`, fg: `hsl(${hue} 60% 32%)` };
}
